import React from 'react';
import { Box, Flex, Text } from '@qga/roo-ui/components';
import pluralize from 'pluralize';
import { equals } from 'ramda';

import type {
  FlightJourney,
  SplitFlightOptions,
} from '@shared/v2/services/types/api/parsedResponse';

import { useCustomerSelection } from '@/components/CustomerSelectionProvider';
import useFlightOptionInteractionsEvent from '@/v2/hooks/useFlightOptionInteractionsEvent';
import { Charges } from '@/v2/services/availability/types/api/parsedResponse';
import FlightCTA from '../../../components/FlightCTA';
import FlightsSegment from '../../../components/FlightsSegment';
import FlightsSegmentBox from '../../../components/FlightsSegmentBox';
import {
  FLIGHT_DIRECTION,
  FlightDirection,
} from '../../../constants/flightDirection';
import { getFlightChargeData, savePoints } from '../../../utils';

export interface SelectedFlightOption {
  outbound: FlightJourney | undefined;
  inbound: FlightJourney | undefined;
}

interface FlightListProps {
  selectedFlightOption: SelectedFlightOption;
  setSelectedFlightOption: {
    setOutbound: (flight: FlightJourney) => void;
    setInbound: (flight: FlightJourney) => void;
  };
  flightDirection: FlightDirection;
  flightOptions: SplitFlightOptions | undefined;
  charges: Charges;
  offerId?: string;
}

const FlightList = ({
  selectedFlightOption,
  setSelectedFlightOption,
  flightDirection = FLIGHT_DIRECTION.Departing,
  flightOptions,
  charges,
  offerId,
}: FlightListProps) => {
  const options = {
    [FLIGHT_DIRECTION.Departing]: flightOptions?.outbound,
    [FLIGHT_DIRECTION.Returning]: flightOptions?.inbound,
  }[flightDirection];

  const selectedOption = {
    [FLIGHT_DIRECTION.Departing]: selectedFlightOption.outbound,
    [FLIGHT_DIRECTION.Returning]: selectedFlightOption.inbound,
  }[flightDirection];

  const onSelect = {
    [FLIGHT_DIRECTION.Departing]: setSelectedFlightOption.setOutbound,
    [FLIGHT_DIRECTION.Returning]: setSelectedFlightOption.setInbound,
  }[flightDirection];

  const { points: allPoints, setPoints } = useCustomerSelection();
  const savePointsToSessionStorage = savePoints(setPoints);

  const { handleInvokeFlightOptionInteractionsEvent } =
    useFlightOptionInteractionsEvent({
      availablePackageCount:
        flightDirection === FLIGHT_DIRECTION.Departing
          ? flightOptions?.outbound?.length
          : flightOptions?.inbound?.length,
      flightDirection,
    });

  return (
    <Box>
      <Text fontWeight="bold">{`${pluralize(
        'flight',
        options?.length,
        true,
      )} available`}</Text>
      <Flex flexDirection="column" pt={[3, null, null]}>
        {options?.map((option, index: number) => {
          const isSelected = equals(selectedOption, option);

          const currentAmount = charges.total.amount;

          const { currentFraction, newFraction, newAmount } =
            getFlightChargeData({
              points: allPoints,
              offerId,
              currentTotalAmount: currentAmount,
              additionalAmount: option.charges.total.amount,
              currentAdditionalAmount: selectedOption!.charges.total.amount,
            });

          return (
            <FlightsSegmentBox
              key={`${flightDirection}-${index}`}
              data-testid="FLIGHT_SEGMENT_ORDER"
              isSelected={isSelected}
            >
              <FlightsSegment flights={option.flightSegments} />
              <FlightCTA
                isSelected={isSelected}
                charges={{
                  selectedOption: selectedOption!.charges,
                  option: option.charges,
                  offer: charges,
                  current: {
                    amount: currentAmount,
                    fraction: currentFraction,
                  },
                }}
                onSelected={() => {
                  // We need to update session storage every time a flight is selected because the
                  // PointsPlusPay component requires that info to render correct breakdown
                  savePointsToSessionStorage({
                    offerId,
                    amount: newAmount,
                    fraction: newFraction,
                  });

                  handleInvokeFlightOptionInteractionsEvent({
                    flightOption: option,
                    packagePrice: newAmount,
                  });

                  onSelect(option);
                }}
              />
            </FlightsSegmentBox>
          );
        })}
      </Flex>
    </Box>
  );
};

export default FlightList;
